/* eslint-disable react/no-danger */
import React from 'react';
import PropTypes from 'prop-types';

import { trans } from '@spotahome/soyuz/client';
import { CloseButton } from '@spotahome/ui-library';
import Tracking from '@spotahome/soyuz-tracking';

// lodash utils
import uniq from 'lodash/uniq';
import isEmpty from 'lodash/isEmpty';

import cityToggleConfig from '@spotahome/marketplace-common/src/config/cityToggleConfig';

import CheckBox from '@spotahome/marketplace-common/src/LEGACY/javascripts/ui/form/form-checkbox';
import GroupToggle from '@spotahome/marketplace-common/src/LEGACY/javascripts/ui/form/form-group-toggle';

import FilterGroup from '../FilterGroup';

import { filtersShape } from '../../shapes/filters';

import ClearFiltersButton from './ClearFiltersButton';

import NUMBER_BATHROOMS_TOGGLE from './bathrooms-config';
import BED_TYPE_TOGGLE from './beds-config';
import { FEATURES } from './features-config';
import { RULES } from './rules-config';

const SHOW_PROPERTIES_WITH_NO_DEPOSIT = 1;
const SHOW_ALL_PROPERTIES = 0;
const SHOW_VERIFIED_PROPERTIES = 1;

const isValueChecked = (arr = [], value) => arr.includes(value);

const Filters = props => {
  const getSelectedBathrooms = () => {
    return props.filters['bathrooms[]'].join(',');
  };

  const handleToggleCheckbox = (name, value, isChecked) => {
    let options = props.filters[name];
    if (isChecked) {
      options = [...options, value];
    } else {
      options = options.filter(option => option !== value);
    }

    props.onFiltersUpdate({
      [name]: uniq(options)
    });
  };

  const handleNoDepositFilterCheckbox = isChecked =>
    props.onFiltersUpdate({
      noDeposit: isChecked
        ? SHOW_PROPERTIES_WITH_NO_DEPOSIT
        : SHOW_ALL_PROPERTIES
    });

  const isNoDepositChecked = () => {
    const noDeposit = parseInt(props.filters.noDeposit, 10);
    return noDeposit === SHOW_PROPERTIES_WITH_NO_DEPOSIT;
  };

  const isFeatureChecked = value => {
    return isValueChecked(props.filters['features[]'], value);
  };

  const isVerifiedChecked = () => {
    const verified = parseInt(props.filters.verified, 10);
    return verified === SHOW_VERIFIED_PROPERTIES;
  };

  const handleBathroomClick = (name, value, isChecked) =>
    handleToggleCheckbox('bathrooms[]', value, isChecked);

  const handleBedTypesClick = (name, value, isChecked) => {
    let selectedValues = [];
    if (!isEmpty(props.filters.bed)) {
      selectedValues = props.filters.bed.split(',');
    }

    if (isChecked) {
      selectedValues.push(value);
      selectedValues = uniq(selectedValues);
    } else {
      selectedValues = selectedValues.filter(
        currentVal => currentVal !== value
      );
    }

    props.onFiltersUpdate({ bed: selectedValues.join(',') });
  };

  const handleVerifiedFilterCheckbox = isChecked => {
    props.onFiltersUpdate({
      verified: isChecked ? SHOW_VERIFIED_PROPERTIES : SHOW_ALL_PROPERTIES
    });
  };

  const handleToggleFilterFeaturesCheckbox = value => {
    return isChecked => {
      handleToggleCheckbox('features[]', value, isChecked);
    };
  };

  const handleClearFilters = () => {
    props.onClearFilters();
    Tracking.ga.sendEvent('Search', 'clear-all-filters-expanded');
  };

  const renderAdvancedSearch = () => (
    <FilterGroup
      classModifier="property-type-checkboxes"
      label={trans('search.filter.advanced-search')}
    >
      <CheckBox
        id="verified"
        labelTitle={trans('search.filter.verified.title')}
        label={trans('search.filter.verified.text')}
        onChange={handleVerifiedFilterCheckbox}
        tracking="ga-search-form-verified"
        checked={isVerifiedChecked()}
      />

      {cityToggleConfig(props.cityId).isNoDepositSupported && (
        <CheckBox
          id="noDeposit"
          data-cy="no-deposit-filter"
          labelTitle={trans('search.filter.no-deposit.title')}
          label={trans('search.filter.no-deposit.text')}
          onChange={handleNoDepositFilterCheckbox}
          checked={isNoDepositChecked()}
          soyuzTracking={{
            eventCategory: 'Search',
            eventAction: 'Filters No Deposit'
          }}
        />
      )}
    </FilterGroup>
  );

  const renderBedTypeFilter = () => (
    <FilterGroup classModifier="toggle" label={trans('search.filter.type_bed')}>
      <GroupToggle
        onChange={handleBedTypesClick}
        selectedValues={props.filters.bed}
        options={BED_TYPE_TOGGLE}
        key="specific-key-bed"
      />
    </FilterGroup>
  );

  const renderBathroomFilter = () => (
    <FilterGroup
      classModifier="align-label-to-first-row"
      label={trans('search.filter.bathrooms')}
    >
      <GroupToggle
        onChange={handleBathroomClick}
        selectedValues={getSelectedBathrooms()}
        options={NUMBER_BATHROOMS_TOGGLE(
          trans('search.filter.bathrooms.2-or-more')
        )}
        key="specific-key-bathrooms"
      />
      <div style={{ position: 'relative', top: '-10px' }}>
        <CheckBox
          key="ensuiteRoom"
          id="ensuiteRoom-id"
          cy="bathroom-private-bathroom"
          label={trans('search.filter.private_bathroom')}
          onChange={handleToggleFilterFeaturesCheckbox('ensuiteRoom')}
          checked={isFeatureChecked('ensuiteRoom')}
          tracking="ga-search-form-ensuiteRoom"
        />
      </div>
    </FilterGroup>
  );

  const renderFeatureFilter = () => (
    <FilterGroup
      classModifier="checkboxes"
      label={trans('search.filter.features')}
    >
      {FEATURES.map(({ name, labelKey, tracking, cy }) => (
        <CheckBox
          key={name}
          id={`${name}-id`}
          label={trans(labelKey)}
          onChange={handleToggleFilterFeaturesCheckbox(name)}
          checked={isFeatureChecked(name)}
          tracking={tracking}
          cy={cy}
        />
      ))}
    </FilterGroup>
  );

  const renderHouseRulesFilter = () => (
    <FilterGroup
      classModifier="checkboxes"
      label={trans('search.filter.rules')}
    >
      {RULES.map(({ name, labelKey, tracking, cy }) => (
        <CheckBox
          key={name}
          id={`${name}-id`}
          label={trans(labelKey)}
          onChange={handleToggleFilterFeaturesCheckbox(name)}
          checked={isFeatureChecked(name)}
          tracking={tracking}
          cy={cy}
        />
      ))}
    </FilterGroup>
  );

  const renderFiltersHeader = activeFiltersCount => (
    <div className="filters__header">
      <div className="filters__title">
        <h1 className="filters__title-text">{trans('filters.title')} </h1>
        <ClearFiltersButton
          activeFiltersCount={activeFiltersCount}
          onClearFilters={handleClearFilters}
        />
      </div>

      <CloseButton onClick={props.onCloseFilters} />
    </div>
  );

  return (
    <div data-test="expanded-full-filters">
      {renderFiltersHeader(props.activeFiltersCount)}
      {renderFeatureFilter()}
      {renderHouseRulesFilter()}
      {renderAdvancedSearch()}
      {renderBedTypeFilter()}
      {renderBathroomFilter()}
    </div>
  );
};

Filters.propTypes = {
  filters: filtersShape,
  cityId: PropTypes.string.isRequired,
  onFiltersUpdate: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func,
  activeFiltersCount: PropTypes.number,
  onCloseFilters: PropTypes.func
};

Filters.defaultProps = {
  filters: {
    'bathrooms[]': [],
    bed: '',
    budget: '',
    'features[]': [],
    instantBooking: 0,
    'move-in': '',
    'move-out': '',
    page: 1,
    poi: '',
    sortBy: 'bestMatch',
    'topology[]': [],
    'type[]': [],
    verified: 0,
    isBestChoice: 0,
    'areaId[]': [],
    plan: '',
    'rentalType[]': [],
    moveOutFrom: '',
    moveOutTo: '',
    moveInFrom: '',
    moveInTo: ''
  },
  onClearFilters: () => {},
  onCloseFilters: () => {},
  activeFiltersCount: 0
};

export default Filters;
